import { BreadcrumbItem } from '../../../shared/components/breadcrumbs/breadcrumbs.component';
import { SavedListsService } from '../list-page/saved-lists.service';
import { SavedProductList } from '../list-page/list-page.models';
import { SEARCH_PRODUCTS, GET_PRODUCT_RETAIL_PRICE_RANGES } from "../product-list/product-list.graphql";
import { getDisplayVolumeDiscountTiers } from "../../../shared/utils/volume-discount.utils";
import { DateUtils } from "../../../shared/utils/date.utils";
import {
    Component,
    OnDestroy,
    OnInit,
    TemplateRef,
    ViewChild,
    HostListener,
} from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { Subscription, of } from "rxjs";
import { catchError, filter, map, switchMap, tap, withLatestFrom } from "rxjs/operators";
import { Meta, Title } from "@angular/platform-browser";

import {
    AddToCartMutation,
    AddToCartMutationVariables,
    GetProductDetailQuery,
    GetProductDetailQueryVariables,
} from "../../../common/generated-types";
import { notNullOrUndefined } from "../../../common/utils/not-null-or-undefined";
import { normalizeGtin } from "../../../common/utils/gtin.utils";
import { filterVariantsBySupplyTypes } from "../../../common/utils/supply-type-filter.utils";
import { DataService } from "../../providers/data/data.service";
import { NotificationService } from "../../providers/notification/notification.service";
import { StateService } from "../../providers/state/state.service";

import {
    ADD_TO_CART,
    GET_PRODUCT_DETAIL,
    GET_CURRENT_CUSTOMER,
    GET_PRODUCT_RETAIL_PRICE_INFO,
    GET_PDP_SETTINGS,
} from "./product-detail.graphql";
import { ActiveService } from "../../providers/active/active.service";
import { SUPPLY_TYPE_FILTER_QUERY_PARAM } from "../supply-type-filter/supply-type-filter.constants";
import { getInitialQuantity } from "../../../shared/utils/pricing.utils";

type Variant = NonNullable<
    GetProductDetailQuery["product"]
>["variants"][number];
type Collection = NonNullable<
    GetProductDetailQuery["product"]
>["collections"][number];

type ProductCustomFields = {
    gtin?: string | null;
    brand?: string | null;
    quantityPerBox?: number | null;
};

type ProductDetail = NonNullable<GetProductDetailQuery["product"]> & {
    customFields?: ProductCustomFields;
};

type RetailPriceMarket = {
    domainId: number;
    marketplace: string;
    countryName: string;
    currency: string;
    asin?: string | null;
    retailAvg30?: number | null;
    status: string;
    keepaLastUpdate?: string | null;
    retrievedAt: string;
};

type ProductRetailPriceInfo = {
    ean?: string | null;
    updatedAt?: string | null;
    prices: RetailPriceMarket[];
};

type ProductRetailPriceInfoQuery = {
    productRetailPriceInfo: ProductRetailPriceInfo;
};

type DetailVariant = Variant & import("../../../shared/types/variant.types").TableVariant & {
    customFields?: ProductCustomFields;
    pricingProduct?: {
        minQ?: number | null;
        maxQ?: number | null;
        brand?: string | null;
    } | null;
};

function isUsableBrand(brand?: string | null): brand is string {
    const value = (brand ?? "").trim();
    if (!value) {
        return false;
    }
    const normalized = value.toLowerCase();
    return (
        normalized !== "-" &&
        normalized !== "n/a" &&
        normalized !== "na" &&
        normalized !== "pozostałe" &&
        normalized !== "pozostale"
    );
}

@Component({
    selector: "vsf-product-detail",
    templateUrl: "./product-detail.component.html",
    styleUrls: ["./product-detail.component.scss"],
})
export class ProductDetailComponent implements OnInit, OnDestroy {
    product: ProductDetail | undefined;
    productLoading = true;
    productLoadFailed = false;
    productNotFound = false;
    selectedAsset: { id: string; preview: string };
    qtyInCart: { [id: string]: number } = {};
    selectedVariant: DetailVariant;
    qty = 1;
    breadcrumbs: Collection["breadcrumbs"] | null = null;
    inFlight = false;
    quantityMap: { [variantId: string]: number } = {};
    supplierId: string | null = null;
    filteredVariantsBySupplierId: DetailVariant[] = [];
    displayVariants: DetailVariant[] = [];
    cheapestVariant: DetailVariant[] | [];
    activeSupplyTypes: string[] = [];
    retailPriceInfo: ProductRetailPriceInfo | null = null;
    retailPricesLoading = false;
    retailPricesExpanded = false;

    get productCategoryBreadcrumbs(): Collection["breadcrumbs"] { return (this.breadcrumbs ?? []).filter(b => b.id !== "product-name" && b.slug !== "__root_collection__"); }

    get breadcrumbItems(): BreadcrumbItem[] {
        return [{ label: 'Home', link: '/' }, { label: 'Health & Beauty Catalog', link: '/category/products' },
            ...this.productCategoryBreadcrumbs.map(crumb => ({ label: crumb.name, link: ['/category', crumb.slug] })),
            ...(this.product ? [{ label: this.product.name }] : [])];
    }

    get signInReturnUrl(): string { return this.router.url; }

    maxMov: number | null = null;
    offerAvailability = "all";
    selectedConditions: string[] = [];
    openFilter: string | null = null;
    @HostListener("window:resize")
    onViewportChange(): void { this.openFilter = null; }
    get hasHotOffer(): boolean { return this.displayVariants.some(v => v.pricing?.isLimitedTimeOffer); }
    newListName = "";
    savedLists: SavedProductList[] = [];
    savedListsBusy = false;
    savedListsError = '';
    private loadSavedLists(): void {
        this.savedListsBusy = true; this.savedListsError = '';
        this.savedListsService.read().subscribe({ next: lists => { this.savedLists = lists; this.savedListsBusy = false; },
            error: () => { this.savedListsBusy = false; this.savedListsError = 'Your lists could not be loaded. Please retry.'; } });
    }
    isSavedInList(index: number): boolean { return !!this.savedLists[index]?.products.some(p => p.id === this.product?.id || !!p.gtin && normalizeGtin(p.gtin) === normalizeGtin(this.product?.customFields?.gtin)); }
    private savedProduct() {
        return { id: this.product!.id, name: this.product!.name, slug: this.route.snapshot.paramMap.get("slug")!, gtin: this.product!.customFields?.gtin || undefined };
    }
    toggleSavedList(index: number): void {
        if (!this.product || this.savedListsBusy) return;
        const list = this.savedLists[index];
        const products = this.isSavedInList(index) ? list.products.filter(p => p.id !== this.product!.id && (!p.gtin || normalizeGtin(p.gtin) !== normalizeGtin(this.product!.customFields?.gtin))) : [...list.products, this.savedProduct()];
        this.savedListsBusy = true; this.savedListsError = '';
        this.savedListsService.update({ ...list, products }).subscribe({ next: updated => { this.savedLists = this.savedLists.map((item, i) => i === index ? updated : item); this.savedListsBusy = false; },
            error: () => { this.savedListsBusy = false; this.savedListsError = 'Could not save. Reopen the page to refresh your lists before retrying.'; } });
    }
    createSavedList(): void {
        const name = this.newListName.trim();
        if (!this.product || !name || this.savedListsBusy) return;
        this.savedListsBusy = true; this.savedListsError = '';
        this.savedListsService.create({ name, products: [this.savedProduct()] }).subscribe({ next: list => {
            this.savedLists = [...this.savedLists, list]; this.newListName = ''; this.savedListsBusy = false;
        }, error: () => { this.savedListsBusy = false; this.savedListsError = 'Could not create this list. Check its name and try again.'; } });
    }
    @HostListener("window:venduro-open-product-lists")
    openSavedLists(): void { this.openFilter = "lists"; document.querySelector(".pdp__save")?.scrollIntoView({ behavior: "smooth", block: "center" }); }
    get otherAvailable(): number { return this.otherOffers.reduce((sum, v) => sum + (v.stockLevels ?? []).reduce((n, stock) => n + Math.max(0, stock.stockOnHand - stock.stockAllocated), 0), 0); }

    readonly conditionOptions = [{value: "new", label: "New"}, {value: "clearance", label: "Clearance"}, {value: "damaged", label: "Damaged packaging"}, {value: "short", label: "Short-dated"}];
    toggleFilter(name: string): void { this.openFilter = this.openFilter === name ? null : name; }
    @HostListener("document:click", ["$event"])
    closeFilterOnOutsideClick(event: MouseEvent): void { if (!(event.target as Element)?.closest?.(".pdp__filter")) this.openFilter = null; }
    @HostListener("document:keydown.escape")
    closeFilterOnEscape(): void { this.openFilter = null; }
    toggleCondition(value: string): void { this.selectedConditions = this.selectedConditions.includes(value) ? this.selectedConditions.filter(c => c !== value) : [...this.selectedConditions, value]; }
    conditionLabel(value: string): string { return this.conditionOptions.find(c => c.value === value)?.label ?? value; }
    conditionCount(value: string): number { return this.displayVariants.filter(v => this.matchesCondition(v, value)).length; }
    formatMov(value: number): string { return new Intl.NumberFormat("en-GB", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(value / 100); }
    private matchesCondition(v: DetailVariant, condition: string): boolean {
        const pricing = v.pricing;
        if (!pricing) return false;
        const clearance = (pricing.goodsStatus ?? "").toLowerCase().includes("clearance");
        return condition === "new" ? !pricing.isDamagedPackaging && !pricing.isShortBbd && !clearance : condition === "damaged" ? !!pricing.isDamagedPackaging : condition === "decoded" ? !!pricing.isDecoded : condition === "clearance" ? clearance : !!pricing.isShortBbd;
    }
    deliveryWeeks: number | null = null;
    showAllOffers = false;
    mobileFiltersOpen = false;
    startSellingUrl: string | null = null;
    relatedProducts: Array<{ id: string; slug: string; name: string; brand: string; gtin: string; preview: string; offers: number; price: number | null; saving: number }> = [];
    private relatedSub = new Subscription();
    private settingsSub = new Subscription();
    get offerPreviewCount(): number { return typeof window !== "undefined" && window.matchMedia("(max-width: 767px)").matches ? 3 : 5; }

    get activeOfferFilterCount(): number {
        return Number(this.maxMov !== null) + Number(this.deliveryWeeks !== null) + Number(this.offerAvailability !== "all") + this.selectedConditions.length;
    }
    get mobileLowestPrice(): number { return this.lowestOffers.length ? this.lowestTierPrice(this.lowestOffers[0]) : 0; }
    addLowestOffer(): void {
        const variant = this.lowestOffers[0];
        if (variant) this.addToCart(variant, this.quantityMap[variant.id] ?? variant.pricingProduct?.minQ ?? variant.moq ?? 1);
    }
    sellViaVenduro(): void {
        if (this.startSellingUrl) { window.open(this.startSellingUrl, "_blank", "noopener,noreferrer"); return; }
        document.querySelector<HTMLButtonElement>("#contact-us button")?.click();
    }
    private loadRelatedProducts(): void {
        this.relatedSub.unsubscribe();
        this.relatedSub = new Subscription();
        this.relatedProducts = [];
        const brand = this.displayBrand;
        const productId = this.product?.id;
        if (!brand || !productId) return;
        this.relatedSub.add(this.dataService.query<any>(SEARCH_PRODUCTS, { input: { term: brand, groupByProduct: true, take: 30 } }).subscribe({
            next: ({ search }) => {
                this.relatedProducts = (search?.items ?? []).filter((item: any) => item.productId !== productId && String(item.customProductMappings?.brand ?? "").toLowerCase() === brand.toLowerCase()).slice(0, 6).map((item: any) => ({
                    id: item.productId, slug: item.slug, name: item.productName, brand,
                    gtin: item.customProductMappings?.gtin ?? "", preview: item.productAsset?.preview ?? "assets/icons/venduro-icon-light-grey.svg",
                    offers: (item.variants ?? []).filter((v: any) => v.priceWithTax > 0).length,
                    price: item.cheapestVariant?.priceWithTax > 0 ? item.cheapestVariant.priceWithTax : null, saving: 0
                }));
                if (!this.relatedProducts.length) return;
                this.relatedSub.add(this.dataService.query<any>(GET_PRODUCT_RETAIL_PRICE_RANGES, { productIds: this.relatedProducts.map(item => item.id) }).subscribe({
                    next: ({ productRetailPriceRanges }) => {
                        this.relatedProducts = this.relatedProducts.map(item => {
                            const retail = productRetailPriceRanges.find((r: any) => r.productId === item.id && r.currency === "EUR");
                            return { ...item, saving: item.price && retail?.min > 0 ? Math.max(0, Math.round((1 - item.price / retail.min) * 100)) : 0 };
                        });
                    }, error: () => {}
                }));
            }, error: () => {}
        }));
    }


    get sortedOffers(): DetailVariant[] {
        return this.displayVariants.filter(v => v.pricing &&
            (this.maxMov === null || (v.pricing.mov ?? 0) <= this.maxMov) &&
            (!this.selectedConditions.length || this.selectedConditions.some(c => this.matchesCondition(v, c))) &&
            (this.deliveryWeeks === null || this.matchesDelivery(v)) &&
            (this.offerAvailability === "all" ||
                (this.offerAvailability === "pre-order" ? v.pricing.supplyType === "pre-order" :
                    v.pricing.supplyType !== "pre-order" && (v.stockLevels ?? []).some(s => s.stockOnHand > s.stockAllocated))))
            .sort((a, b) => this.lowestTierPrice(a) - this.lowestTierPrice(b));
    }
    private matchesDelivery(variant: DetailVariant): boolean {
        const display = DateUtils.getVariantShippingDateDisplay(variant).toLowerCase();
        const match = display.match(/(?:est\.\s*)?(\d+)(?:\s*-\s*(\d+))?\s+weeks?/);
        return !!match && Number(match[2] ?? match[1]) <= this.deliveryWeeks!;
    }
    private lowestTierPrice(variant: DetailVariant): number {
        const discounts = getDisplayVolumeDiscountTiers(variant.pricing?.volumeDiscounts)
            .filter(t => t.discountPercent < 100 && (this.maxMov === null || t.orderValueMin * 100 <= this.maxMov));
        return Math.round(variant.priceWithTax * (1 - Math.max(0, ...discounts.map(t => t.discountPercent)) / 100));
    }
    get lowestOffers(): DetailVariant[] { return this.sortedOffers.slice(0, 1); }
    get otherOffers(): DetailVariant[] { return this.sortedOffers.slice(1); }
    get visibleOtherOffers(): DetailVariant[] { return this.showAllOffers ? this.otherOffers : this.otherOffers.slice(0, this.offerPreviewCount); }
    get retailSaving(): number {
        const retail = this.pricedRetailMarkets.filter(p => p.currency === "EUR").map(p => p.retailAvg30!);
        const wholesale = this.lowestOffers.length ? this.lowestTierPrice(this.lowestOffers[0]) : null;
        return retail.length && wholesale ? Math.max(0, Math.round((1 - wholesale / Math.min(...retail)) * 100)) : 0;
    }

    private readonly marketFlagSrcByCode: Record<string, string> = {
        DE: "../../../../assets/icons/flag-de.svg",
        FR: "../../../../assets/icons/flag-fr.svg",
        ES: "../../../../assets/icons/flag-es.svg",
        IT: "../../../../assets/icons/flag-it.svg",
        UK: "../../../../assets/icons/flag-uk.svg",
        GB: "../../../../assets/icons/flag-uk.svg",
    };
    @ViewChild("addedToCartTemplate", { static: true })
    private addToCartTemplate: TemplateRef<any>;
    private sub: Subscription;
    private retailPriceSub: Subscription;
    currentCustomerId: string | null = null;

    /** Catalog brand first; otherwise first usable offer brand. */
    get displayBrand(): string | null {
        const catalogBrand = this.product?.customFields?.brand;
        if (isUsableBrand(catalogBrand)) {
            return catalogBrand.trim();
        }

        const variants =
            this.displayVariants?.length > 0
                ? this.displayVariants
                : this.product?.variants ?? [];

        for (const variant of variants as DetailVariant[]) {
            const offerBrand = variant.pricingProduct?.brand;
            if (isUsableBrand(offerBrand)) {
                return offerBrand.trim();
            }
        }

        return null;
    }

    constructor(
        private dataService: DataService,
        private savedListsService: SavedListsService,
        private stateService: StateService,
        private notificationService: NotificationService,
        private activeService: ActiveService,
        private route: ActivatedRoute,
        private router: Router,
        private meta: Meta,
        private titleService: Title
    ) {}

    private updateMetaTags(product: ProductDetail) {
        if (!product) return;
        this.titleService.setTitle(`${product.name} | Venduro B2B Marketplace`);

        this.meta.updateTag({
            name: "description",
            content:
                product.description ||
                `Buy ${product.name} in bulk at wholesale prices on Venduro B2B Marketplace`,
        });

        this.meta.updateTag({ property: "og:title", content: product.name });
        this.meta.updateTag({
            property: "og:description",
            content: product.description,
        });
        if (product.featuredAsset?.preview) {
            this.meta.updateTag({
                property: "og:image",
                content: product.featuredAsset.preview,
            });
        }

        if (this.selectedVariant) {
            this.meta.updateTag({
                property: "product:price:amount",
                content: this.selectedVariant.priceWithTax.toString(),
            });
            this.meta.updateTag({
                property: "product:price:currency",
                content: "EUR",
            });
        }

        const structuredData = {
            "@context": "https://schema.org/",
            "@type": "Product",
            name: product.name,
            description: product.description,
            image: product.featuredAsset?.preview,
            sku: this.selectedVariant?.sku,
            gtin: normalizeGtin(
                this.selectedVariant?.customFields?.gtin ??
                    this.product?.customFields?.gtin,
            ),
            offers:
                this.product?.variants.map((variant) => ({
                    "@type": "Offer",
                    priceCurrency: "EUR",
                    price: variant.priceWithTax,
                    availability: "https://schema.org/InStock",
                    seller: {
                        "@type": "Organization",
                        name: "Venduro",
                    },
                })) || [],
        };

        const scriptElement = document.querySelector("#productStructuredData");
        if (scriptElement) {
            scriptElement.textContent = JSON.stringify(structuredData);
        } else {
            const script = document.createElement("script");
            script.setAttribute("id", "productStructuredData");
            script.setAttribute("type", "application/ld+json");
            script.textContent = JSON.stringify(structuredData);
            document.head.appendChild(script);
        }
    }

    ngOnInit() {
        this.settingsSub.add(this.dataService.query<{ storefrontSettings: { startSellingUrl: string | null } }>(GET_PDP_SETTINGS).subscribe(({ storefrontSettings }) => this.startSellingUrl = storefrontSettings?.startSellingUrl ?? null));
        const lastCollectionSlug$ = this.stateService.select(
            (state) => state.lastCollectionSlug
        );
        const productSlug$ = this.route.paramMap.pipe(
            map((paramMap) => paramMap.get("slug")),
            filter(notNullOrUndefined)
        );

        this.route.paramMap.subscribe((paramMap) => {
            this.supplierId = paramMap.get("supplierId");
        });

        this.route.queryParamMap.subscribe((paramMap) => {
            const param = paramMap.get(SUPPLY_TYPE_FILTER_QUERY_PARAM);
            this.activeSupplyTypes = param
                ? param.split(",").map((v) => v.trim()).filter(Boolean)
                : [];
            this.applyVariantFilters();
        });

        this.dataService
            .query<any>(GET_CURRENT_CUSTOMER)
            .subscribe(({ activeCustomer }) => {
                if (activeCustomer) {
                    this.currentCustomerId = activeCustomer?.id ?? null;
                    this.loadSavedLists();
                }
            });

        this.sub = productSlug$
            .pipe(
                switchMap((slug) => {
                    this.productLoading = true;
                    this.productLoadFailed = false;
                    this.productNotFound = false;
                    this.product = undefined;
                    this.breadcrumbs = [];
                    this.relatedProducts = [];
                    this.relatedSub.unsubscribe();
                    return this.dataService.query<
                        GetProductDetailQuery,
                        GetProductDetailQueryVariables
                    >(GET_PRODUCT_DETAIL, {
                        slug,
                    }).pipe(
                        tap(({ product }) => {
                            this.productLoading = false;
                            this.productNotFound = !product;
                        }),
                        catchError(() => {
                            this.productLoading = false;
                            this.productLoadFailed = true;
                            return of({ product: null });
                        }),
                    );
                }),
                map((data) => data.product),
                filter(notNullOrUndefined),
                withLatestFrom(lastCollectionSlug$)
            )
            .subscribe(([product, lastCollectionSlug]) => {
                const productDetail = product as ProductDetail;
                const customFields: ProductCustomFields = {
                    ...(productDetail.customFields || {}),
                    gtin: normalizeGtin(productDetail.customFields?.gtin),
                };
                this.product = { ...productDetail, customFields };
                this.loadRetailPriceInfo(product.id);
                this.applyVariantFilters();
                this.showAllOffers = false;
                this.mobileFiltersOpen = false;
                this.loadRelatedProducts();

                if (this.product?.featuredAsset) {
                    this.selectedAsset = this.product.featuredAsset;
                }
                this.selectedVariant = this.displayVariants[0] ?? product.variants[0];
                const collection = this.getMostRelevantCollection(
                    product.collections,
                    lastCollectionSlug
                );
                this.breadcrumbs = collection
                    ? [...collection.breadcrumbs]
                    : [];
                this.breadcrumbs = [
                    ...this.breadcrumbs,
                    {
                        id: "product-name",
                        name: product.name,
                        slug: product.name,
                    },
                ];

                this.displayVariants.forEach((variant) => {
                    this.quantityMap[variant.id] = getInitialQuantity(
                        variant as DetailVariant,
                        this.product,
                    );
                });

                if (this.displayVariants.length > 0) {
                    this.cheapestVariant = [...this.displayVariants]
                        .sort((a, b) => a.priceWithTax - b.priceWithTax)
                        .slice(0, 1);
                } else {
                    this.cheapestVariant = [];
                }

                this.updateMetaTags(this.product);
            });

        this.activeService.activeOrder$.subscribe((order) => {
            this.qtyInCart = {};
            for (const line of order?.lines ?? []) {
                this.qtyInCart[line.productVariant.id] = line.quantity;
            }
        });
    }

    private applyVariantFilters(): void {
        if (!this.product?.variants) {
            this.displayVariants = [];
            this.filteredVariantsBySupplierId = [];
            return;
        }

        let variants: Variant[] = [...this.product.variants];
        if (this.activeSupplyTypes.length > 0) {
            variants = filterVariantsBySupplyTypes(
                variants as Array<{
                    supplyType?: string | null;
                    pricing?: { supplyType?: string | null };
                }>,
                this.activeSupplyTypes,
            ) as Variant[];
        }

        this.displayVariants = variants.filter((variant) => variant.priceWithTax > 0);
        this.filteredVariantsBySupplierId = this.displayVariants.filter((variant) => {
            const pricing = (variant as Variant & { pricing: { id: string } }).pricing;
            return pricing?.id === this.supplierId;
        });
    }

    ngOnDestroy() {
        this.settingsSub.unsubscribe();
        this.relatedSub.unsubscribe();
        if (this.sub) {
            this.sub.unsubscribe();
        }
        if (this.retailPriceSub) {
            this.retailPriceSub.unsubscribe();
        }
        const scriptElement = document.querySelector("#productStructuredData");
        if (scriptElement) {
            scriptElement.remove();
        }
    }

    navigateToCart() {
        this.router.navigate(["/cart"]);
    }

    addToCart(variant: Variant, qty: number) {
        const pricing = (variant as Variant & { pricing: any }).pricing;

        const minimum = (variant as DetailVariant).pricingProduct?.minQ ?? (variant as DetailVariant).moq ?? 1;
        if (!Number.isInteger(qty) || qty < minimum) {
            this.notificationService
                .error(
                    `You must order at least ${minimum} units of this product.`
                )
                .subscribe();
            return;
        }

        this.inFlight = true;
        this.dataService
            .mutate<AddToCartMutation, AddToCartMutationVariables>(
                ADD_TO_CART,
                {
                    variantId: variant.id,
                    qty,
                }
            )
            .subscribe({
                next: ({ addItemToOrder }) => {
                this.inFlight = false;
                switch (addItemToOrder.__typename) {
                    case "Order":
                        this.stateService.setState(
                            "activeOrderId",
                            addItemToOrder ? addItemToOrder.id : null
                        );
                        if (variant) {
                            this.notificationService
                                .notify({
                                    title: "Added to cart",
                                    type: "info",
                                    duration: 3000,
                                    templateRef: this.addToCartTemplate,
                                    templateContext: {
                                        variant,
                                        quantity: qty,
                                    },
                                })
                                .subscribe();
                        }
                        break;
                    case "OrderModificationError":
                    case "OrderLimitError":
                    case "NegativeQuantityError":
                    case "InsufficientStockError":
                        this.notificationService
                            .error(addItemToOrder.message)
                            .subscribe();
                        break;
                }
                },
                error: (err) => {
                    this.inFlight = false;
                    console.error("AddToCart failed:", err);
                    this.notificationService
                        .error(
                            "Could not add to cart. The request timed out or the server is busy — please try again."
                        )
                        .subscribe();
                },
            });
    }

    viewCartFromNotification(closeFn: () => void) {
        this.stateService.setState("cartDrawerOpen", true);
        closeFn();
    }

    private getMostRelevantCollection(
        collections: Collection[],
        lastCollectionSlug: string | null
    ) {
        const lastCollection = collections.find(
            (c) => c.slug === lastCollectionSlug
        );
        if (lastCollection) {
            return lastCollection;
        }
        return collections.slice().sort((a, b) => {
            if (a.breadcrumbs.length < b.breadcrumbs.length) {
                return 1;
            }
            if (a.breadcrumbs.length > b.breadcrumbs.length) {
                return -1;
            }
            return 0;
        })[0];
    }

    copyToClipboard(text: string, event: Event) {
        event.preventDefault();
        navigator.clipboard
            .writeText(text)
            .then(() => {
                this.notificationService
                    .notify({
                        title: "Product code copied to clipboard",
                        type: "info",
                        duration: 2000,
                    })
                    .subscribe();
            })
            .catch(() => {
                this.notificationService
                    .error("Failed to copy product code")
                    .subscribe();
            });
    }

    toggleRetailPrices(): void {
        this.retailPricesExpanded = !this.retailPricesExpanded;
    }

    get pricedRetailMarkets(): RetailPriceMarket[] {
        return (this.retailPriceInfo?.prices ?? []).filter(
            (price) =>
                price.status === "FOUND" &&
                price.retailAvg30 != null &&
                price.retailAvg30 > 0
        );
    }

    get hasRetailPrices(): boolean {
        return this.pricedRetailMarkets.length > 0;
    }

    get foldedRetailPriceRange(): string | null {
        if (!this.pricedRetailMarkets || this.pricedRetailMarkets.length === 0) {
            return null;
        }
        const groups = new Map<string, number[]>();
        for (const market of this.pricedRetailMarkets) {
            const currency = market.currency || "EUR";
            groups.set(currency, [...(groups.get(currency) ?? []), market.retailAvg30!]);
        }
        return Array.from(groups).map(([currency, amounts]) => {
            const min = Math.min(...amounts), max = Math.max(...amounts);
            return min === max ? this.formatRetailPrice(min, currency) : `${this.formatRetailPrice(min, currency)}–${this.formatRetailPrice(max, currency)}`;
        }).join(" · ");
    }

    get preferredRetailAsin(): string | null {
        const matched = (this.retailPriceInfo?.prices ?? []).filter(
            (price) =>
                (price.status === "FOUND" || price.status === "NO_PRICE") &&
                !!price.asin?.trim()
        );
        return (
            matched.find((price) => price.marketplace === "DE")?.asin?.trim() ??
            matched[0]?.asin?.trim() ??
            null
        );
    }

    marketFlagSrc(marketplace: string): string | null {
        const code = (marketplace || "").trim().toUpperCase();
        return this.marketFlagSrcByCode[code] ?? null;
    }

    formatRetailPrice(price: number | null | undefined, currency: string): string {
        if (price == null) {
            return "—";
        }
        return new Intl.NumberFormat("en-GB", {
            style: "currency",
            currency,
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
        }).format(price / 100);
    }

    retailPriceTooltip(price: RetailPriceMarket): string {
        const raw = price.keepaLastUpdate || price.retrievedAt;
        if (!raw) {
            return "";
        }
        const date = new Date(raw);
        if (Number.isNaN(date.getTime()) || date.getTime() <= 0) {
            return "";
        }
        const datePart = date.toLocaleDateString("en-US", {
            month: "long",
            day: "numeric",
            year: "numeric",
        });
        const timePart = date.toLocaleTimeString("en-US", {
            hour: "numeric",
            minute: "2-digit",
        });
        return `Updated at ${datePart}, ${timePart}`;
    }

    private loadRetailPriceInfo(productId: string): void {
        if (this.retailPriceSub) {
            this.retailPriceSub.unsubscribe();
        }
        this.retailPricesLoading = true;
        this.retailPriceInfo = null;
        this.retailPriceSub = this.dataService
            .query<ProductRetailPriceInfoQuery, { productId: string }>(
                GET_PRODUCT_RETAIL_PRICE_INFO,
                { productId }
            )
            .subscribe({
                next: ({ productRetailPriceInfo }) => {
                    this.retailPriceInfo = productRetailPriceInfo;
                    this.retailPricesLoading = false;
                },
                error: () => {
                    this.retailPriceInfo = null;
                    this.retailPricesLoading = false;
                },
            });
    }
}
